import React from "react";
import { FormControl, FormHelperText, Typography, Box } from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

/**
 * Reusable dropdown using MUI Select.
 * Label above (no floating). Styles from theme.
 *
 * @param {string} name
 * @param {string} label
 * @param {string|number} value - selected value
 * @param {function} onChange - (e) => {}
 * @param {Array<{ value: string|number, label: string }>} options
 * @param {string} placeholder - when nothing selected
 * @param {boolean} required
 * @param {string} error
 * @param {boolean} disabled
 */
const Dropdown = ({
  name,
  label,
  value = "",
  onChange,
  options = [],
  placeholder = "Select",
  required = false,
  error = "",
  disabled = false,
  id,
  sx = {},
  ...rest
}) => {
  const inputId = id || (name ? `dropdown-${name}` : undefined);

  // Determine if we should show the placeholder
  const hasValue = value !== "" && value !== null && value !== undefined;

  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      disabled={disabled}
      required={required}
      sx={sx}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={inputId}
          variant="body2"
          fontWeight={600}
          sx={{
            display: "block",
            mb: 0.5,
            color: error ? "error.main" : "text.secondary",
            transition: "color 0.2s ease-in-out",
            fontSize: "0.875rem",
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
      )}
      <Select
        id={inputId}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        variant="outlined"
        displayEmpty
        sx={{
          height: 40,
          borderRadius: "10px",
          backgroundColor: "#fff",
          "& .MuiSelect-select": {
            py: 0,
            height: 40,
            display: "flex",
            alignItems: "center",
            fontSize: "0.875rem",
            color: hasValue ? "text.primary" : "text.secondary",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e5e7eb",
            borderRadius: "10px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#38915c",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#38915c",
            borderWidth: "1px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ef4444 !important",
          },
        }}
        renderValue={(v) => {
          if (v === "" || v === null || v === undefined) {
            return <span style={{ color: "#9ca3af" }}>{placeholder}</span>;
          }
          const opt = options.find((o) => String(o.value) === String(v));
          return opt ? opt.label : v;
        }}
        MenuProps={{
          disableScrollLock: true,
          sx: { zIndex: 10001 },
          PaperProps: { 
            sx: { 
              zIndex: 10001,
              borderRadius: "10px",
              marginTop: "4px",
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
              border: "1px solid #f3f4f6",
              "& .MuiMenuItem-root": {
                fontSize: "0.875rem",
                py: 1,
                "&:hover": {
                  backgroundColor: "#f0fdf4",
                  color: "#38915c",
                },
                "&.Mui-selected": {
                  backgroundColor: "#38915c",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#2d7a4d",
                  },
                },
              },
            } 
          },
        }}
        {...rest}
      >
        {placeholder && (
          <MenuItem value="" disabled sx={{ display: "none" }}>
            {placeholder}
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FormHelperText sx={{ ml: 0, mt: 0.5, color: "#ef4444" }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default Dropdown;
